import { useState } from 'react';
import clsx from 'clsx';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { FLUXO_MODELO_EXTRA_LABELS } from '../../../models/mensagensContato.modelos';
import { useDocumentosParaMensagem, useModeloMensagemMutations, useModelosMensagem } from '../../../hooks/data';
import MensagemModeloEditor from './MensagemModeloEditor';

interface MensagensContatoConfigProps {
  onVoltar: () => void;
}

const NOVO = 'novo';

/**
 * Configurações → Mensagens de contato: os 7 modelos dos botões "Chamar no WhatsApp/Teams"
 * (mesmo texto nos dois), escolhidos sozinhos pela situação da Movimentação, e os modelos
 * criados pelo SI ("+ Novo modelo"), que aparecem como alternativa no seletor "Mensagem".
 */
export default function MensagensContatoConfig({ onVoltar }: MensagensContatoConfigProps) {
  const { modelos, extras, isLoading } = useModelosMensagem();
  const { data: documentos = [] } = useDocumentosParaMensagem();
  const { salvar, restaurar, salvarExtra, excluirExtra } = useModeloMensagemMutations();
  const [selecionado, setSelecionado] = useState<string>('envio_codigo');

  const padrao = modelos.find((m) => m.id === selecionado) ?? null;
  const extra = extras.find((m) => m.id === selecionado) ?? null;
  // Modelo do SI excluído (ou ainda carregando) — volta pro primeiro.
  const atual = padrao ?? extra ?? (selecionado === NOVO ? null : modelos[0]);

  const chip = (id: string, rotulo: string, marca?: string) => (
    <button
      key={id}
      type="button"
      onClick={() => setSelecionado(id)}
      className={clsx(
        'rounded-full px-3 py-1.5 text-xs transition-colors',
        selecionado === id ? 'bg-primary-600 font-semibold text-white' : 'border border-gray-200 text-gray-700 hover:bg-primary-50'
      )}
    >
      {rotulo}
      {marca}
    </button>
  );

  return (
    <div className="space-y-4">
      <button type="button" onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Voltar
      </button>
      <div>
        <h3 className="font-montserrat text-lg font-semibold text-gray-900">Mensagens de contato</h3>
        <p className="text-sm text-gray-500">
          Textos dos botões &quot;Chamar no WhatsApp&quot; e &quot;Chamar no Teams&quot; da solicitação (o mesmo texto nos dois).
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase text-gray-400">Automáticos (escolhidos pela situação)</p>
        <div className="flex flex-wrap gap-1.5">{modelos.map((m) => chip(m.id, m.titulo, m.personalizado ? ' ✎' : ''))}</div>
        <p className="text-xs font-medium uppercase text-gray-400">Criados pelo SI (escolha manual na solicitação)</p>
        <div className="flex flex-wrap gap-1.5">
          {extras.map((m) => chip(m.id, m.titulo))}
          <button
            type="button"
            onClick={() => setSelecionado(NOVO)}
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border border-dashed px-3 py-1.5 text-xs font-semibold',
              selecionado === NOVO ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-primary-50'
            )}
          >
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" /> Novo modelo
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-xl bg-gray-100" />
      ) : selecionado === NOVO ? (
        <>
          <p className="text-xs text-gray-500">
            Aparece no seletor &quot;Mensagem&quot; das Movimentações do fluxo escolhido — a automática continua sendo a sugerida.
          </p>
          <MensagemModeloEditor
            key={NOVO}
            extra
            inicial={{ titulo: '', fluxo: 'todos', texto: 'Olá, {primeiro_nome}!\n', documentoIds: [] }}
            documentos={documentos}
            salvando={salvarExtra.isPending}
            onSalvar={(v) =>
              salvarExtra.mutate(
                { titulo: v.titulo!.trim(), fluxo: v.fluxo ?? 'todos', texto: v.texto, documentoIds: v.documentoIds },
                { onSuccess: (id) => setSelecionado(id) }
              )
            }
          />
        </>
      ) : extra ? (
        <>
          <p className="text-xs text-gray-500">
            Criado pelo SI · aparece em: <b>{FLUXO_MODELO_EXTRA_LABELS[extra.fluxo]}</b>
          </p>
          <MensagemModeloEditor
            key={`${extra.id}:${extra.titulo}:${extra.fluxo}:${extra.texto}:${extra.documentoIds.join(',')}`}
            extra
            inicial={{ titulo: extra.titulo, fluxo: extra.fluxo, texto: extra.texto, documentoIds: extra.documentoIds }}
            documentos={documentos}
            salvando={salvarExtra.isPending}
            onSalvar={(v) =>
              salvarExtra.mutate({
                id: extra.id,
                titulo: v.titulo!.trim(),
                fluxo: v.fluxo ?? 'todos',
                texto: v.texto,
                documentoIds: v.documentoIds,
              })
            }
            acaoSecundaria={{
              label: '🗑 Excluir modelo',
              disabled: excluirExtra.isPending,
              onClick: () => {
                if (!window.confirm(`Excluir o modelo "${extra.titulo}"?`)) return;
                excluirExtra.mutate(extra.id, { onSuccess: () => setSelecionado('envio_codigo') });
              },
            }}
          />
        </>
      ) : (
        atual &&
        'quando' in atual && (
          <>
            <p className="text-xs text-gray-500">
              Usado em: <b>{atual.quando}</b>
              {atual.personalizado && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Personalizado</span>
              )}
            </p>
            <MensagemModeloEditor
              key={`${atual.id}:${atual.texto}:${atual.documentoIds.join(',')}`}
              extra={false}
              inicial={{ texto: atual.texto, documentoIds: atual.documentoIds }}
              documentos={documentos}
              salvando={salvar.isPending}
              onSalvar={(v) => salvar.mutate({ id: atual.id, texto: v.texto, documentoIds: v.documentoIds })}
              acaoSecundaria={{
                label: '↺ Restaurar o texto padrão',
                disabled: !atual.personalizado || restaurar.isPending,
                onClick: () => restaurar.mutate(atual.id),
              }}
            />
          </>
        )
      )}
    </div>
  );
}
